/**
 * A header button that opens a dark dropdown under itself.
 *
 * The dropdown is portalled to `document.body` and positioned from the
 * button's rectangle (the header row clips its overflow, so a dropdown inside
 * it would be cut off), kept within the viewport, and closed by a click
 * outside, Escape (capture phase — the views underneath do not see it), a
 * resize, or a scroll.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconTextButton, IconTextButtonProps } from './IconTextButton';
import { useEscape } from './useEscape';
import './designerHeader.css';

export interface DesignerMenuProps extends Omit<IconTextButtonProps, 'children' | 'onClick'> {
  /** The dropdown's content; given `close` to call after a choice. */
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
  menuLabel?: string;
  minWidth?: number;
  /** Called when the menu opens or closes. */
  onOpenChange?: (open: boolean) => void;
}

const MARGIN = 8;

export const DesignerMenu: React.FC<DesignerMenuProps> = ({ children, menuLabel, minWidth = 180, onOpenChange, ...button }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });

  const close = useCallback(() => setOpen(false), []);
  useEffect(() => { onOpenChange?.(open); }, [open, onOpenChange]);
  useEscape(open, close);

  // Under the button, centred on it, inside the viewport.
  const place = useCallback(() => {
    const b = buttonRef.current?.getBoundingClientRect();
    const p = panelRef.current;
    if (!b || !p) return;
    const w = p.offsetWidth;
    const h = p.offsetHeight;
    const left = Math.max(MARGIN, Math.min(b.left + b.width / 2 - w / 2, window.innerWidth - w - MARGIN));
    let top = b.bottom + 6;
    if (top + h > window.innerHeight - MARGIN) top = Math.max(MARGIN, b.top - h - 6);
    setPos({ left, top });
  }, []);
  useLayoutEffect(() => { if (open) place(); }, [open, place, children]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (buttonRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onAway = () => setOpen(false);
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('resize', onAway);
    window.addEventListener('scroll', onAway, true);
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('resize', onAway);
      window.removeEventListener('scroll', onAway, true);
    };
  }, [open]);

  return (
    <>
      <IconTextButton
        ref={buttonRef}
        {...button}
        active={button.active || open}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      />
      {open && createPortal(
        <div
          ref={panelRef}
          role="menu"
          aria-label={menuLabel ?? button.label}
          className="dh-menu"
          style={{ left: pos.left, top: pos.top, minWidth }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {typeof children === 'function' ? children(close) : children}
        </div>,
        document.body,
      )}
    </>
  );
};

export interface DesignerMenuItemProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon?: React.ReactNode;
  label: React.ReactNode;
  active?: boolean;
  danger?: boolean;
  /** Something at the right of the label (a small button, a count). */
  trailing?: React.ReactNode;
}

export const DesignerMenuItem: React.FC<DesignerMenuItemProps> = ({ icon, label, active, danger, trailing, className, ...rest }) => (
  <button
    type="button"
    role={active !== undefined ? 'menuitemradio' : 'menuitem'}
    aria-checked={active}
    className={['dh-menu-item', active ? 'is-active' : '', danger ? 'is-danger' : '', className ?? ''].filter(Boolean).join(' ')}
    {...rest}
  >
    {icon}
    <span className="dh-menu-item-label">{label}</span>
    {trailing}
  </button>
);

export const DesignerMenuDivider: React.FC = () => <div className="dh-menu-divider" role="separator" />;
export const DesignerMenuHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="dh-menu-heading">{children}</div>;

export default DesignerMenu;
